import { Injectable, OnDestroy } from "@angular/core";
import { IUserDto } from "core/dto";
import { IDisposable, SSFormGroup } from "core/dynamic-form";
import { filterAllTruthyInTuple, pairBehaviorSubjects, subjectFromObs } from "core/rx-js";
import { ObjectUtils } from "core/utils";
import { BehaviorSubject, Subject, combineLatest, debounceTime, filter, map, switchMap, takeUntil } from "rxjs";
import { IActor } from "../../../../dto-map/i-actor";
import { RemoteSplugParametersConfigurationService } from "../../../../remotes/remote-splug-parameters-configuration.service";
import { SelectActivitiesStepService } from "../../select-activities-step.service";
import { ActivityCollectionService } from "../activity-collection.service";

enum ActorType {
  External = 1,
  Internal = 2
}


@Injectable()
export class ActivityItemService implements OnDestroy {

  private readonly _destroy = new Subject<void>();

  readonly activityIdSubject = new BehaviorSubject<string>(undefined);
  get activityId() {
    return this.activityIdSubject.value;
  }

  readonly disposables: IDisposable[] = [];
  readonly selectedUserSubject = new BehaviorSubject<IUserDto>(undefined)

  readonly splugInfoOpenedSubject = new BehaviorSubject<boolean>(false);
  readonly mainFormGroupSubject = new BehaviorSubject<SSFormGroup>(null);
  readonly activitiesSubject = this._activityCollectionService.activitiesSubject;
  readonly triedNextObs = this._selectActivitiesStepService.triedNextObs;

  readonly activitySubject = subjectFromObs({
    initialValue: undefined,
    until: this._destroy,
    observable: combineLatest([
      this.activityIdSubject,
      this.activitiesSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        map(([activityId, activities]) => activities.find(({ id }) => id === activityId))
      )
  })

  readonly splugParametersConfigurationsSubject = this._remoteSplugParametersConfigurationService.localCollectionStore.stateSubject;

  readonly showErrorObs = combineLatest([
    this.triedNextObs,
    this.activitySubject.pipe(map(activity => activity?.actor?.id)),
    this.splugParametersConfigurationsSubject
  ])
    .pipe(
      map(([triedNext, actorId, splugParametersConfigurations]) => {
        if (!triedNext) {
          return false;
        }

        const actorConfigurations = splugParametersConfigurations?.filter((configuration) => configuration.actorId === actorId);
        return !actorConfigurations?.length;
      })
    )

  constructor(
    private readonly _activityCollectionService: ActivityCollectionService,
    private readonly _selectActivitiesStepService: SelectActivitiesStepService,
    private readonly _remoteSplugParametersConfigurationService: RemoteSplugParametersConfigurationService
  ) {



  }


  pairUserSelectedSubject(autocompleteSelectedItemSubject: BehaviorSubject<any>) {
    const disposable = pairBehaviorSubjects(autocompleteSelectedItemSubject, this.selectedUserSubject);
    this.disposables.push(disposable);
  }


  private _balanceActivities() {

    this.mainFormGroupSubject
      .pipe(
        takeUntil(this._destroy),
        filter(Boolean),
        switchMap((mainFormGroup) => mainFormGroup.valueChanges),
        debounceTime(10)
      )
      .subscribe((actor: IActor) => {

        const activities = [...this.activitiesSubject.value];

        const prevActivityIdx = activities.findIndex(({ id }) => this.activityId === id);
        const prevActivity = activities[prevActivityIdx];

        if (!prevActivity?.actor) {
          return;
        }

        if (ObjectUtils.areObjectKeysSame(actor, prevActivity.actor, ['email', 'firstName', 'lastName', 'mobile'])) {
          return;
        }

        activities[prevActivityIdx] = {
          ...prevActivity,
          actor
        };

        this.activitiesSubject.next(activities);
      })

    this.activitiesSubject
      .pipe(
        takeUntil(this._destroy),
        map(activities => activities.find(({ id }) => this.activityId === id)),
        filter(Boolean)
      )
      .subscribe((activity) => {

        const actor: IActor = this.mainFormGroupSubject.value?.value;

        if (!activity?.actor) {
          return;
        }

        if (ObjectUtils.areObjectKeysSame(actor, activity.actor, ['email', 'firstName', 'lastName', 'mobile'])) {
          return;
        }

        const { email, firstName, lastName, mobile } = activity?.actor || {};

        this.mainFormGroupSubject.value.setValue({
          email,
          firstName,
          lastName,
          mobile
        });

      })


  }

  ngOnDestroy(): void {
    this.selectedUserSubject.complete();
    this.splugInfoOpenedSubject.complete();

    this.disposables.forEach((disposable) => disposable.dispose());

    this._destroy.next();
    this._destroy.complete();
  }


}
